const hasMatchingDetailVersion = (row, detail) => (
  detail?.history_id === row?.history_id
  && detail?.row_version === row?.row_version
  && Array.isArray(detail?.selections)
);

const ESTABLISHED_CATEGORY_LABELS = Object.freeze({
  DOMESTIC: '국내형',
  INTERNATIONAL: '해외형',
  LIVE: '라이브',
  ESPORTS: 'E-스포츠',
  SPECIAL: 'E-스포츠',
  CROSS: '크로스',
});
const ESTABLISHED_STATUS_LABELS = Object.freeze({
  PENDING: '처리중',
  CONFIRMED: '확정',
  WON_PENDING: '당첨 대기',
  WON: '당첨',
  LOST: '낙첨',
  VOID: '무효',
  HALF_WIN: '반적중',
  HALF_LOSE: '반미적중',
  PARTIAL_WON: '부분당첨',
  PARTIAL_LOST: '부분미적중',
  PARTIAL: '부분정산',
  CANCELLED: '취소',
  REJECTED: '거절',
  FAILED: '실패',
});
const establishedCategory = (category) => category === 'ESPORTS' ? 'SPECIAL' : category;
const normalizedStatus = (value) => {
  const status = String(value || '').trim().toUpperCase();
  if (status === 'HALF_LOSS') return 'HALF_LOSE';
  if (status === 'CANCELED') return 'CANCELLED';
  return status;
};

const establishedDisplayStatus = (row) => {
  const explicitStatus = normalizedStatus(row.display_status);
  if (explicitStatus) return explicitStatus;

  const status = normalizedStatus(row.status);
  const payoutVariant = normalizedStatus(row.final_payout_variant);
  if (status === 'WON' && payoutVariant === 'HALF_WIN') return 'HALF_WIN';
  if (status === 'LOST' && payoutVariant === 'HALF_LOSE') return 'HALF_LOSE';
  if (status === 'WON' && payoutVariant === 'MIXED') return 'PARTIAL_WON';
  if (status === 'LOST' && payoutVariant === 'MIXED') return 'PARTIAL_LOST';
  if (status === 'PARTIAL') return 'PARTIAL';

  // Compatibility only for responses predating the structured display fields.
  if (status === 'WON' && row.status_label === '반적중') return 'HALF_WIN';
  if (status === 'LOST' && row.status_label === '반미적중') return 'HALF_LOSE';
  return status;
};

const establishedStatusLabel = (row) => {
  const displayStatus = establishedDisplayStatus(row);
  return ESTABLISHED_STATUS_LABELS[displayStatus] ?? row.status_label ?? displayStatus;
};

const parseSnapshot = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const scorePair = (value) => {
  if (Array.isArray(value) && value.length >= 2) return { home: value[0], away: value[1] };
  if (typeof value === 'string') {
    const matched = value.trim().match(/^(.+?)\s*:\s*(.+)$/);
    return matched ? { home: matched[1], away: matched[2] } : null;
  }
  const snapshot = parseSnapshot(value);
  if (!snapshot) return null;
  const score = parseSnapshot(snapshot.score) || snapshot;
  const home = score.home ?? score.home_score ?? score.score_home ?? score.homeScore;
  const away = score.away ?? score.away_score ?? score.score_away ?? score.awayScore;
  return home == null || away == null ? null : { home, away };
};

const capabilitiesFor = (row) => row.archive_state === 'IMMUTABLE'
  ? {
      ...row.capabilities,
      can_cancel: false,
      can_void: false,
      can_manual_settle: false,
      can_restate: false,
      can_approve_win: false,
      can_override_selection: false,
    }
  : row.capabilities;

export const adaptEstablishedSportsBetHistorySelection = (selection) => {
  const boardSnapshot = parseSnapshot(selection.market_display_snapshot);
  const finalScore = scorePair(selection.final_score);
  const liveScore = scorePair(selection.live_score_snapshot);
  return {
    id: selection.selection_ref,
    selection_ref: selection.selection_ref,
    match_name: selection.fixture_name,
    selection_name: selection.selection_name,
    selection_display_name_snapshot: selection.selection_name,
    effective_selection_display_name: selection.selection_name,
    market_name: selection.market_name,
    market_name_ko: selection.market_name,
    effective_market_name: selection.market_name,
    effective_market_name_ko: selection.market_name,
    line_snapshot: selection.line,
    odds_snapshot: selection.accepted_odds,
    effective_odds_snapshot: selection.accepted_odds,
    market_display_snapshot: boardSnapshot,
    effective_market_display_snapshot: boardSnapshot,
    score_at_bet: selection.score_at_bet,
    live_score_snapshot: selection.live_score_snapshot ?? null,
    fixture_status: selection.fixture_status ?? null,
    final_score: selection.final_score,
    result: selection.outcome,
    effective_result: selection.outcome,
    result_label: selection.outcome_label,
    effective_settled_at: selection.settled_at,
    has_active_selection_override: selection.manual_override === true,
    match: {
      start_time: selection.fixture_start_at,
      status: selection.fixture_status || (finalScore ? 'ENDED' : liveScore ? 'LIVE' : ''),
      sport: selection.sport_name ? { name_ko: selection.sport_name } : null,
      final_score_home: finalScore?.home ?? null,
      final_score_away: finalScore?.away ?? null,
      live_score_home: liveScore?.home ?? null,
      live_score_away: liveScore?.away ?? null,
    },
  };
};

const selectionsFor = (row, detail) => {
  if (hasMatchingDetailVersion(row, detail)) {
    return detail.selections.map(adaptEstablishedSportsBetHistorySelection);
  }
  return row.preview_selection
    ? [adaptEstablishedSportsBetHistorySelection(row.preview_selection)]
    : [];
};

export const adaptEstablishedSportsBetHistory = (row, detail = null) => {
  const selections = selectionsFor(row, detail);
  const displayStatus = establishedDisplayStatus(row);
  return {
    id: row.history_id,
    history_id: row.history_id,
    row_version: row.row_version,
    bet_uid: row.bet_uid,
    bet_type: row.bet_type,
    selection_count: row.selection_count,
    status: row.status,
    display_status: displayStatus,
    final_payout_variant: row.final_payout_variant ?? null,
    status_label: establishedStatusLabel(row),
    source_status: row.source_status ?? row.status,
    stake: row.stake,
    total_odds: row.display_total_odds,
    display_total_odds: row.display_total_odds,
    has_api_display_total_odds: true,
    potential_win: row.display_potential_win,
    display_potential_win: row.display_potential_win,
    actual_win: row.display_actual_win,
    display_actual_win: row.display_actual_win,
    created_at: row.accepted_at,
    settled_at: row.settled_at,
    is_live_bet: row.category === 'LIVE',
    display_betting_category: establishedCategory(row.category),
    display_betting_category_label: ESTABLISHED_CATEGORY_LABELS[row.category] ?? row.category_label,
    has_active_selection_override: selections
      .some((selection) => selection.has_active_selection_override),
    archive_state: row.archive_state,
    archive_read_only: row.archive_state === 'IMMUTABLE',
    capabilities: capabilitiesFor(row),
    selections,
  };
};
